<template>
  <div class="ecard-management">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2 class="page-title">
        <i class="el-icon-tickets"></i>
        新文科模块电子卡管理
      </h2>
    </div>

    <!-- 搜索和筛选区域 -->
    <div class="search-section">
      <el-card class="search-card">
        <el-form :model="searchForm" :inline="true" class="search-form">
          <el-form-item label="关键词">
            <el-input
              v-model="searchForm.keyword"
              placeholder="搜索电子卡名称或密码"
              clearable
              style="width: 200px"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="学校名称">
            <el-input
              v-model="searchForm.schoolName"
              placeholder="搜索学校名称"
              clearable
              style="width: 200px"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="searchForm.state" placeholder="选择状态" clearable style="width: 120px">
              <el-option label="启用" :value="1" />
              <el-option label="禁用" :value="0" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              搜索
            </el-button>
            <el-button icon="el-icon-refresh" @click="handleReset">
              重置
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>

    <!-- 电子卡列表 -->
    <div class="table-section">
      <el-card class="table-card">
        <div slot="header" class="table-header">
          <span class="table-title">电子卡列表</span>
           <div class="table-actions">
             <el-button type="primary" icon="el-icon-plus" @click="handleAddEcard">
               新增电子卡
             </el-button>
             <el-button type="success" icon="el-icon-refresh" @click="handleRefresh">
               刷新
             </el-button>
           </div>
        </div>

         <el-table
           v-loading="loading"
           :data="ecardList"
           stripe
           border
           style="width: 100%; table-layout: fixed;"
           empty-text="暂无电子卡数据"
         >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="name" label="电子卡名称" width="120" show-overflow-tooltip align="center" />
          <el-table-column prop="pwd" label="密码" width="160" align="center">
            <template slot-scope="scope">
              <span class="password-text">{{ scope.row.pwd }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="schoolName" label="学校名称" width="140" show-overflow-tooltip align="center" />
          <el-table-column prop="validDate" label="最晚绑卡日期" width="140" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.validDate) }}
            </template>
          </el-table-column>
          <el-table-column prop="endDate" label="失效日期" width="140" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.endDate) }}
            </template>
          </el-table-column>
          <el-table-column prop="bindMax" label="绑定上限" width="120" align="center" />
          <el-table-column prop="bindNow" label="已绑定" width="120" align="center">
            <template slot-scope="scope">
              <span :class="scope.row.bindNow >= scope.row.bindMax ? 'text-danger' : 'text-success'">
                {{ scope.row.bindNow }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="state" label="状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.state === 1 ? 'success' : 'danger'" size="small">
                {{ scope.row.state === 1 ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" width="200" show-overflow-tooltip align="center" />
          <el-table-column label="操作" align="center">
            <template slot-scope="scope">
              <el-button
                type="success"
                size="mini"
                @click="handleViewBindRecords(scope.row)"
              >
                绑卡记录
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-wrapper">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.current"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pagination.size"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
          />
        </div>
      </el-card>
    </div>

     <!-- 电子卡新增/编辑对话框 -->
     <el-dialog
       :title="dialogTitle"
       :visible.sync="addDialogVisible"
       width="600px"
       :close-on-click-modal="false"
       @close="handleDialogClose"
     >
       <el-form
         ref="ecardForm"
         :model="ecardForm"
         :rules="ecardRules"
         label-width="120px"
       >
         <el-form-item label="电子卡名称" prop="name">
           <el-input v-model="ecardForm.name" placeholder="请输入电子卡名称" />
         </el-form-item>
         <el-form-item label="电子卡密码" prop="pwd">
           <el-input v-model="ecardForm.pwd" placeholder="请输入电子卡密码" readonly>
             <el-button slot="append" icon="el-icon-refresh" @click="generatePassword" :loading="passwordGenerating">
               生成密码
             </el-button>
           </el-input>
         </el-form-item>
         <el-form-item label="学校名称" prop="schoolId">
           <el-select
             v-model="ecardForm.schoolId"
             placeholder="请选择学校"
             filterable
             style="width: 100%"
             @focus="loadSchoolList"
           >
             <el-option
               v-for="school in schoolList"
               :key="school.id"
               :label="school.name"
               :value="school.id"
             />
           </el-select>
         </el-form-item>
         <el-form-item label="最晚绑卡日期" prop="validDate">
           <el-date-picker
             v-model="ecardForm.validDate"
             type="datetime"
             placeholder="选择最晚绑卡日期"
             format="yyyy-MM-dd HH:mm:ss"
             value-format="yyyy-MM-dd HH:mm:ss"
             style="width: 100%"
           />
         </el-form-item>
         <el-form-item label="失效日期" prop="endDate">
           <el-date-picker
             v-model="ecardForm.endDate"
             type="datetime"
             placeholder="选择失效日期"
             format="yyyy-MM-dd HH:mm:ss"
             value-format="yyyy-MM-dd HH:mm:ss"
             style="width: 100%"
           />
         </el-form-item>
         <el-form-item label="绑定上限" prop="bindMax">
           <el-input-number
             v-model="ecardForm.bindMax"
             :min="1"
             :max="10000"
             placeholder="绑定上限"
             style="width: 100%"
           />
         </el-form-item>
         <el-form-item label="状态" prop="state">
           <el-radio-group v-model="ecardForm.state">
             <el-radio :label="1">启用</el-radio>
             <el-radio :label="0">禁用</el-radio>
           </el-radio-group>
         </el-form-item>
         <el-form-item label="备注">
           <el-input
             v-model="ecardForm.remark"
             type="textarea"
             :rows="3"
             placeholder="请输入备注信息"
           />
         </el-form-item>
       </el-form>
       <div slot="footer" class="dialog-footer">
         <el-button @click="addDialogVisible = false">取消</el-button>
         <el-button type="primary" @click="handleSubmit" :loading="submitLoading">
           确定
         </el-button>
       </div>
     </el-dialog>

     <!-- 绑卡记录对话框 -->
     <el-dialog
       title="电子卡绑卡记录"
       :visible.sync="bindRecordsDialogVisible"
       width="800px"
       :close-on-click-modal="false"
     >
       <div v-if="selectedEcard" class="bind-records">
         <div class="ecard-info">
           <h4>{{ selectedEcard.name }} - 绑卡记录</h4>
           <p>电子卡密码：<span class="password-text">{{ selectedEcard.pwd }}</span></p>
         </div>
         
         <el-table
           v-loading="bindRecordsLoading"
           :data="bindRecordsList"
           stripe
           border
           style="width: 100%"
           empty-text="暂无绑卡记录"
         >
           <el-table-column prop="id" label="ID" width="80" align="center" />
           <el-table-column prop="userName" label="用户名" width="120" show-overflow-tooltip align="center" />
           <el-table-column prop="userMobile" label="手机号" width="130" align="center" />
           <el-table-column prop="ecardIndex" label="绑定序号" width="100" align="center" />
           <el-table-column prop="expireTime" label="到期时间" width="150" align="center">
             <template slot-scope="scope">
               {{ formatDateTime(scope.row.expireTime) }}
             </template>
           </el-table-column>
           <el-table-column prop="createTime" label="绑定时间" width="150" align="center">
             <template slot-scope="scope">
               {{ formatDateTime(scope.row.createTime) }}
             </template>
           </el-table-column>
         </el-table>
         
         <!-- 绑卡记录分页 -->
         <div class="pagination-wrapper" style="margin-top: 16px;">
           <el-pagination
             @size-change="handleBindRecordsSizeChange"
             @current-change="handleBindRecordsCurrentChange"
             :current-page="bindRecordsPagination.current"
             :page-sizes="[10, 20, 50]"
             :page-size="bindRecordsPagination.size"
             layout="total, sizes, prev, pager, next, jumper"
             :total="bindRecordsPagination.total"
           />
         </div>
       </div>
       
       <div slot="footer" class="dialog-footer">
         <el-button @click="bindRecordsDialogVisible = false">关闭</el-button>
       </div>
     </el-dialog>
   </div>
 </template>

<script>
import api from '@/api/api';

export default {
  name: 'EcardManagement',
  data() {
    return {
      loading: false,
      submitLoading: false,
      passwordGenerating: false,
      ecardList: [],
      schoolList: [],
      selectedEcard: null,
      addDialogVisible: false,
      dialogTitle: '新增电子卡',
      bindRecordsDialogVisible: false,
      bindRecordsLoading: false,
      bindRecordsList: [],
      bindRecordsPagination: {
        current: 1,
        size: 10,
        total: 0
      },
      searchForm: {
        keyword: '',
        schoolName: '',
        state: null
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      ecardForm: {
        name: '',
        pwd: '',
        schoolId: null,
        validDate: '',
        endDate: '',
        bindMax: 100,
        state: 1,
        remark: ''
      },
      ecardRules: {
        name: [
          { required: true, message: '请输入电子卡名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        pwd: [
          { required: true, message: '请先生成电子卡密码', trigger: 'blur' }
        ],
        schoolId: [
          { required: true, message: '请选择学校', trigger: 'change' }
        ],
        validDate: [
          { required: true, message: '请选择最晚绑卡日期', trigger: 'change' },
          { validator: this.validateValidDate, trigger: 'change' }
        ],
        endDate: [
          { required: true, message: '请选择失效日期', trigger: 'change' },
          { validator: this.validateEndDate, trigger: 'change' }
        ],
        bindMax: [
          { required: true, message: '请输入绑定上限', trigger: 'blur' }
        ],
        state: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      }
    };
  },
  mounted() {
    this.loadEcardList();
    this.loadSchoolList();
  },
  methods: {
    // 加载学校列表
    async loadSchoolList() {
      try {
        const response = await api.getSchoolList();
        if (response && response.data && response.data.code === 200) {
          this.schoolList = response.data.data || [];
        } else {
          console.warn('获取学校列表失败:', response?.data?.msg);
          // 如果接口不存在，提供一些默认学校选项
          this.schoolList = [
            { id: 1, name: '北京大学' },
            { id: 2, name: '清华大学' },
            { id: 3, name: '复旦大学' },
            { id: 4, name: '上海交通大学' },
            { id: 5, name: '浙江大学' }
          ];
        }
      } catch (error) {
        console.warn('获取学校列表失败:', error);
        // 提供默认学校选项
        this.schoolList = [
          { id: 1, name: '北京大学' },
          { id: 2, name: '清华大学' },
          { id: 3, name: '复旦大学' },
          { id: 4, name: '上海交通大学' },
          { id: 5, name: '浙江大学' }
        ];
      }
    },

    // 加载电子卡列表
    async loadEcardList(showSuccessMessage = false) {
      this.loading = true;
      try {
        const params = {
          pageNum: this.pagination.current,
          pageSize: this.pagination.size,
          keyword: this.searchForm.keyword || undefined,
          schoolName: this.searchForm.schoolName || undefined,
          state: this.searchForm.state !== null ? this.searchForm.state : undefined
        };

         const response = await api.getXinWenKeEcardList(params);
         
         if (response && response.data && response.data.code === 200) {
           // 根据实际API响应结构调整数据解析
           const responseData = response.data.data;
           
           this.ecardList = responseData.list || responseData.records || [];
           this.pagination.total = responseData.total || 0;
           this.pagination.current = responseData.pageNum || responseData.current || 1;
           this.pagination.size = responseData.pageSize || responseData.size || 10;
           
           // 只在明确需要时显示成功提示
           if (showSuccessMessage) {
             this.$message.success('获取电子卡列表成功');
           }
         } else {
           console.error('API响应错误:', response);
           this.$message.error(response?.data?.message || response?.data?.msg || '获取电子卡列表失败');
         }
      } catch (error) {
        console.error('获取电子卡列表失败:', error);
        this.$message.error('获取电子卡列表失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },

     // 手动刷新
     handleRefresh() {
       this.loadEcardList(true);
     },

     // 搜索
    handleSearch() {
      this.pagination.current = 1;
      this.loadEcardList();
    },

    // 重置搜索
    handleReset() {
      this.searchForm = {
        keyword: '',
        schoolName: '',
        state: null
      };
      this.pagination.current = 1;
      this.loadEcardList();
    },

    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.size = size;
      this.pagination.current = 1;
      this.loadEcardList();
    },

    // 当前页改变
    handleCurrentChange(current) {
      this.pagination.current = current;
      this.loadEcardList();
    },


     // 查看绑卡记录
     handleViewBindRecords(ecard) {
       this.selectedEcard = ecard;
       this.bindRecordsDialogVisible = true;
       this.loadBindRecords();
     },

     // 加载绑卡记录
     async loadBindRecords() {
       if (!this.selectedEcard) return;
       
       this.bindRecordsLoading = true;
       try {
         const params = {
           pageNum: this.bindRecordsPagination.current,
           pageSize: this.bindRecordsPagination.size
         };
         
         const response = await api.getXinWenKeEcardBindRecords(this.selectedEcard.id, params);
         
         if (response && response.data && response.data.code === 200) {
           const responseData = response.data.data;
           this.bindRecordsList = responseData.records || [];
           this.bindRecordsPagination.total = responseData.total || 0;
         } else {
           this.$message.error(response?.data?.msg || '获取绑卡记录失败');
         }
       } catch (error) {
         console.error('获取绑卡记录失败:', error);
         this.$message.error('获取绑卡记录失败，请稍后重试');
       } finally {
         this.bindRecordsLoading = false;
       }
     },

     // 绑卡记录分页大小改变
     handleBindRecordsSizeChange(size) {
       this.bindRecordsPagination.size = size;
       this.bindRecordsPagination.current = 1;
       this.loadBindRecords();
     },

     // 绑卡记录当前页改变
     handleBindRecordsCurrentChange(current) {
       this.bindRecordsPagination.current = current;
       this.loadBindRecords();
     },

     // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleDateString('zh-CN');
    },

     // 获取学校名称
     getSchoolName(schoolId) {
       if (!schoolId) return '-';
       const school = this.schoolList.find(s => s.id === schoolId);
       return school ? school.name : '-';
     },

     // 格式化日期时间
     formatDateTime(dateStr) {
       if (!dateStr) return '-';
       const date = new Date(dateStr);
       return date.toLocaleString('zh-CN');
     },

     // 新增电子卡
     handleAddEcard() {
       this.dialogTitle = '新增电子卡';
       this.resetEcardForm();
       this.addDialogVisible = true;
     },

     // 重置表单
     resetEcardForm() {
       this.ecardForm = {
         name: '',
         pwd: '',
         schoolId: null,
         validDate: '',
         endDate: '',
         bindMax: 100,
         state: 1,
         remark: ''
       };
       if (this.$refs.ecardForm) {
         this.$refs.ecardForm.clearValidate();
       }
     },

     // 对话框关闭
     handleDialogClose() {
       this.resetEcardForm();
     },


     // 生成密码
     async generatePassword() {
       this.passwordGenerating = true;
       try {
         const response = await api.generateXinWenKeEcardPassword();
         if (response && response.data && response.data.code === 200) {
           const password = response.data.data;
           this.generatedPassword = password;
           
           // 如果在新增电子卡对话框中，自动填入密码
           if (this.addDialogVisible) {
             this.ecardForm.pwd = password;
           }
           
           this.$message.success('密码生成成功');
         } else {
           this.$message.error(response?.data?.msg || '密码生成失败');
         }
       } catch (error) {
         console.error('生成密码失败:', error);
         this.$message.error('密码生成失败，请稍后重试');
       } finally {
         this.passwordGenerating = false;
       }
     },


     // 验证最晚绑卡日期
     validateValidDate(rule, value, callback) {
       if (!value) {
         callback(new Error('请选择最晚绑卡日期'));
         return;
       }
       
       const validDate = new Date(value);
       const now = new Date();
       
       // 最晚绑卡日期不能早于当前时间
       if (validDate < now) {
         callback(new Error('最晚绑卡日期不能早于当前时间'));
         return;
       }
       
       // 检查是否与失效日期冲突
       if (this.ecardForm.endDate) {
         const endDate = new Date(this.ecardForm.endDate);
         if (validDate >= endDate) {
           callback(new Error('最晚绑卡日期必须早于失效日期'));
           return;
         }
       }
       
       callback();
     },

     // 验证失效日期
     validateEndDate(rule, value, callback) {
       if (!value) {
         callback(new Error('请选择失效日期'));
         return;
       }
       
       const endDate = new Date(value);
       const now = new Date();
       
       // 失效日期不能早于当前时间
       if (endDate <= now) {
         callback(new Error('失效日期必须晚于当前时间'));
         return;
       }
       
       // 检查是否与最晚绑卡日期冲突
       if (this.ecardForm.validDate) {
         const validDate = new Date(this.ecardForm.validDate);
         if (endDate <= validDate) {
           callback(new Error('失效日期必须晚于最晚绑卡日期'));
           return;
         }
       }
       
       callback();
     },

     // 提交表单
     async handleSubmit() {
       try {
         await this.$refs.ecardForm.validate();
         this.submitLoading = true;
         
         // 获取选中的学校信息
         const selectedSchool = this.schoolList.find(school => school.id === this.ecardForm.schoolId);
         if (!selectedSchool) {
           this.$message.error('请选择有效的学校');
           this.submitLoading = false;
           return;
         }
         
         const formData = {
           name: this.ecardForm.name,
           password: this.ecardForm.pwd,
           schoolId: this.ecardForm.schoolId,
           schoolName: selectedSchool.name,
           type: 1, // 单次使用
           bindMax: this.ecardForm.bindMax,
           validDate: this.ecardForm.validDate,
           endDate: this.ecardForm.endDate,
           remark: this.ecardForm.remark || ''
         };
         
         const response = await api.createXinWenKeEcard(formData);
         
         if (response && response.data && response.data.code === 200) {
           this.$message.success('电子卡创建成功');
           this.addDialogVisible = false;
           this.loadEcardList();
         } else {
           this.$message.error(response?.data?.msg || '电子卡创建失败');
         }
         
       } catch (error) {
         console.error('创建电子卡失败:', error);
         this.$message.error('创建电子卡失败，请稍后重试');
       } finally {
         this.submitLoading = false;
       }
     }
   }
 };
</script>

<style scoped>
.ecard-management {
  padding: 0;
}

.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #222;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
}

.page-title i {
  margin-right: 12px;
  color: #f1787f;
  font-size: 28px;
}

.page-description {
  color: #666;
  font-size: 14px;
  margin: 0;
}

.search-section {
  margin-bottom: 24px;
}

.search-card {
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.search-form {
  margin: 0;
}

.table-section {
  margin-bottom: 24px;
}

.table-card {
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  font-size: 18px;
  font-weight: 600;
  color: #222;
  text-align: center;
  flex: 1;
}

.table-actions {
  display: flex;
  gap: 12px;
}

.password-text {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #f1787f;
  background: #f5e9ea;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.text-success {
  color: #67c23a;
  font-weight: 600;
}

.text-danger {
  color: #f56c6c;
  font-weight: 600;
}

.pagination-wrapper {
  margin-top: 24px;
  text-align: right;
}


.dialog-footer {
  text-align: right;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-title {
    font-size: 20px;
  }
  
  .page-title i {
    font-size: 24px;
  }
  
  .search-form {
    display: flex;
    flex-direction: column;
  }
  
  .search-form .el-form-item {
    margin-right: 0;
    margin-bottom: 16px;
  }
  
  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .table-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

/* 表格样式优化 */
.el-table {
  border-radius: 8px;
  overflow: hidden;
}

.el-table th {
  background: #fafbfc;
  color: #222;
  font-weight: 600;
}

.el-table td {
  padding: 12px 0;
}

/* 卡片样式优化 */
.el-card {
  border: 1px solid #f0f0f0;
}

.el-card__header {
  background: #fafbfc;
  border-bottom: 1px solid #f0f0f0;
  padding: 16px 20px;
}

.el-card__body {
  padding: 20px;
}

/* 按钮样式优化 */
.el-button--primary {
  background: #f1787f;
  border-color: #f1787f;
}

.el-button--primary:hover {
  background: #e66a72;
  border-color: #e66a72;
}

.el-button--success {
  background: #67c23a;
  border-color: #67c23a;
}

.el-button--success:hover {
  background: #5daf34;
  border-color: #5daf34;
}

/* 标签样式优化 */
.el-tag--success {
  background: #f0f9ff;
  color: #67c23a;
  border-color: #b3e19d;
}

.el-tag--danger {
  background: #fef0f0;
  color: #f56c6c;
  border-color: #fbc4c4;
}

/* 分页样式优化 */
.el-pagination {
  text-align: right;
}

.el-pagination .el-pager li.active {
  background: #f1787f;
  color: #fff;
}

 .el-pagination .el-pager li:hover {
   color: #f1787f;
 }


 /* 绑卡记录样式 */
 .bind-records {
   padding: 16px 0;
 }

 .ecard-info {
   margin-bottom: 20px;
   padding: 16px;
   background: #f5f7fa;
   border-radius: 6px;
   border-left: 4px solid #f1787f;
 }

 .ecard-info h4 {
   margin: 0 0 8px 0;
   color: #222;
   font-size: 16px;
   font-weight: 600;
 }

 .ecard-info p {
   margin: 0;
   color: #666;
   font-size: 14px;
 }

 .ecard-info .password-text {
   font-family: 'Courier New', monospace;
   font-weight: 600;
   color: #f1787f;
   background: #f5e9ea;
   padding: 2px 6px;
   border-radius: 4px;
   font-size: 12px;
 }

  /* 表格容器样式优化 */
  .table-card {
    overflow-x: auto;
  }

  .table-card .el-card__body {
    padding: 20px 0;
  }

  /* 确保表格占满容器 */
  .el-table {
    width: 100% !important;
  }

  .el-table__body-wrapper {
    width: 100% !important;
  }

  .el-table__header-wrapper {
    width: 100% !important;
  }

 </style>
